/**
 * Simple in-memory cache for data fetching
 * Provides instant UI while fresh data loads in background
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const cache = new Map<string, CacheEntry<unknown>>();
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Get cached data if it exists and hasn't expired
 */
export function getCached<T>(key: string, ttl = DEFAULT_TTL): T | null {
  const entry = cache.get(key);
  if (!entry) return null;

  // Check if expired
  if (Date.now() - entry.timestamp > ttl) {
    cache.delete(key);
    return null;
  }

  return entry.data as T;
}

/**
 * Store data in cache
 */
export function setCache<T>(key: string, data: T): void {
  cache.set(key, { data, timestamp: Date.now() });
}

/**
 * Invalidate cache entries matching a pattern
 */
export function invalidateCache(pattern: string): void {
  for (const key of cache.keys()) {
    if (key.includes(pattern)) {
      cache.delete(key);
    }
  }
}

/**
 * Clear entire cache
 */
export function clearCache(): void {
  cache.clear();
}

/**
 * Get cache key for user data
 */
export function getUserCacheKey(username: string): string {
  return `user:${username.toLowerCase()}`;
}

/**
 * Get cache key for commit data
 */
export function getCommitCacheKey(id: string): string {
  return `commit:${id}`;
}

/**
 * Get cache key for feed data
 */
export function getFeedCacheKey(type: 'main' | 'explore' | 'public' = 'main'): string {
  return `feed:${type}`;
}

/**
 * Get cache key for user commits
 */
export function getUserCommitsCacheKey(userId: string): string {
  return `userCommits:${userId}`;
}

/**
 * Get cache key for tag feed
 */
export function getTagFeedCacheKey(tag: string): string {
  return `tagFeed:${tag.toLowerCase()}`;
}
